import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AuthLayout from '../auth/AuthLayout';
import { authAPI } from '../../utils/crmApi';

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    termsAccepted: false
  });
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Pre-populate email from URL params if provided
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: decodeURIComponent(emailParam)
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Please enter your full name';
    if (!formData.email.trim()) next.email = 'Please enter your email address';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) next.email = 'Please enter a valid email';
    if (!formData.termsAccepted) next.terms = 'Please accept the terms';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await authAPI.signup({
        name: formData.name,
        email: formData.email
      });
      const data = response.data;

      if (data.success) {
        setEmailSent(true);
        toast.success('Verification email sent! 📧');
      } else {
        toast.error(data.message || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Show success message if email was sent
  if (emailSent) {
    return (
      <AuthLayout title="Check your email!" subtitle="We've sent a verification link to the address below.">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <p className="text-xl font-semibold text-primary mb-6">{formData.email}</p>
            <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-xl p-4 mb-6">
              <p className="text-sm text-foreground">Click the link to verify your account and set a password.</p>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Didn't receive it? <button onClick={() => setEmailSent(false)} className="text-primary hover:text-primary/80 font-medium">Try again</button>
            </p>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">← Back to login</Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Join LiveLoveSpain" subtitle="Designed for agencies and partners helping expats move smarter.">
      <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card/80 backdrop-blur-sm py-8 px-4 shadow-2xl sm:rounded-2xl sm:px-10 border border-white/20">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200 bg-muted/50 hover:bg-muted text-foreground"
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-all duration-200 bg-muted/50 hover:bg-muted text-foreground"
                  placeholder="Enter your email address"
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              

              <div className="flex items-start">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary focus:ring-ring border-input rounded mt-1"
                />
                <label htmlFor="termsAccepted" className="ml-3 text-sm text-foreground">
                  I agree to the{' '}
                  <Link to="/terms" className="text-primary hover:text-primary/80 font-medium">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <a href="/privacy" className="text-primary hover:text-primary/80 font-medium">
                    Privacy Policy
                  </a>
                </label>
              </div>
              {errors.terms && <p className="text-xs text-red-600">{errors.terms}</p>}
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="group w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending verification email...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Create account
                    <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                )}
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground">
                We'll send you a verification email to complete your signup
              </p>
            </div>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-card text-muted-foreground font-medium">Already have an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-3 px-4 border border-border rounded-xl shadow-sm text-sm font-semibold text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 transform hover:scale-[1.02]"
              >
                Sign in instead
              </Link>
              <div className="mt-3 text-center">
                <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">
                  Back to website
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Signup;